/**
 * E018-S008: RelationIntegrityService
 *
 * Maintains referential integrity for bidirectional relations across documents.
 * When a row is deleted, this service automatically removes the inverse references
 * in target databases (both InDoc and CrossDoc).
 *
 * Architecture:
 * - Subscribes to rows$ for all databases in the workspace
 * - Subscribes to blockUpdated for cell value changes
 * - Caches relation cell values for deleted rows cleanup
 * - Uses CrossDocRelationService for cross-doc operations
 *
 * This is the "automatic trigger" for row deletion cleanup that was missing.
 */

import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { Service } from '@toeverything/infra';
import { debounceTime, Subject, Subscription } from 'rxjs';

import type { DocsService } from '../../doc';
import type { CrossDocRelationService } from './cross-doc-relation';
import type { DatabaseDiscoveryService } from './database-discovery';

/**
 * Cached relation data for a row.
 * Stored so we can cleanup when the row is deleted.
 */
interface RowRelationCache {
  /** The row ID */
  rowId: string;
  /** Relation column ID -> array of target row IDs */
  relations: Map<
    string,
    {
      targetDocId: string | undefined;
      targetDatabaseId: string;
      inverseFieldId: string | undefined;
      inverseCardinality: 'one' | 'many';
      values: string[];
    }
  >;
}

/**
 * Interface for relation property data extracted from columns.
 */
interface RelationPropertyData {
  targetDocId?: string;
  targetDatabaseId: string;
  inverseFieldId?: string;
  inverseCardinality?: 'one' | 'many';
}

export class RelationIntegrityService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly databaseDiscovery: DatabaseDiscoveryService,
    private readonly crossDocRelation: CrossDocRelationService
  ) {
    super();
    this.setupSubscriptions();
  }

  /**
   * Cache of relation values per database.
   * Structure: databaseId -> Map<rowId, RowRelationCache>
   */
  private readonly relationCache = new Map<
    string,
    Map<string, RowRelationCache>
  >();

  /**
   * Active subscriptions to database rows$.
   * Structure: databaseId -> unsubscribe function
   */
  private readonly databaseSubscriptions = new Map<string, () => void>();

  /**
   * Doc releases to be called on cleanup.
   * Structure: docId -> release function
   */
  private readonly docReleases = new Map<string, () => void>();

  /**
   * Subject to batch cleanup operations.
   */
  private readonly cleanupQueue$ = new Subject<{
    docId: string;
    databaseId: string;
    deletedRowIds: string[];
  }>();

  /**
   * Main subscription to database discovery.
   */
  private discoverySubscription: Subscription | null = null;

  /**
   * Cleanup queue subscription.
   */
  private cleanupSubscription: Subscription | null = null;

  /**
   * Setup initial subscriptions.
   */
  private setupSubscriptions(): void {
    // Subscribe to database discovery to track all databases
    this.discoverySubscription = this.databaseDiscovery.databases$.subscribe(
      databases => {
        const currentDbIds = new Set(databases.map(db => db.blockId));

        // Subscribe to new databases
        for (const db of databases) {
          if (!this.databaseSubscriptions.has(db.blockId)) {
            this.subscribeToDatabase(db.docId, db.blockId);
          }
        }

        // Unsubscribe from removed databases
        for (const dbId of this.databaseSubscriptions.keys()) {
          if (!currentDbIds.has(dbId)) {
            this.unsubscribeFromDatabase(dbId);
          }
        }
      }
    );

    // Process cleanup queue with debounce
    this.cleanupSubscription = this.cleanupQueue$
      .pipe(debounceTime(100))
      .subscribe(({ docId, databaseId, deletedRowIds }) => {
        this.processDeletedRows(docId, databaseId, deletedRowIds).catch(e => {
          console.warn('[RelationIntegrity] Cleanup failed:', e);
        });
      });
  }

  /**
   * Subscribe to a database's rows$ to detect deletions.
   */
  private subscribeToDatabase(docId: string, databaseId: string): void {
    if (this.databaseSubscriptions.has(databaseId)) return;

    try {
      // Open the doc if not already open
      if (!this.docReleases.has(docId)) {
        const { release } = this.docsService.open(docId);
        this.docReleases.set(docId, release);
      }

      const { doc } = this.docsService.open(docId);
      const blockSuiteDoc = doc.blockSuiteDoc;

      if (!blockSuiteDoc) {
        console.warn(`[RelationIntegrity] No blockSuiteDoc for doc ${docId}`);
        return;
      }

      // Find the database block
      const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
      const dbBlock = databases.find(
        (db: { id: string }) => db.id === databaseId
      );

      if (!dbBlock) {
        console.warn(
          `[RelationIntegrity] Database ${databaseId} not found in doc ${docId}`
        );
        return;
      }

      // Create DataSource
      const model =
        (dbBlock as unknown as { model?: DatabaseBlockModel }).model ?? dbBlock;
      const dataSource = createDatabaseBlockDataSource(
        model as DatabaseBlockModel
      );

      // Initialize cache for this database
      this.relationCache.set(databaseId, new Map());
      this.updateRelationCache(databaseId, dataSource);

      // Subscribe to the logical row-id projection. The cache only needs row
      // identities for deletion/new-row detection, not UI-window state.
      let previousRowIds = new Set(dataSource.getLogicalRowIdsSnapshot());

      const subscription = dataSource.rowCount$.subscribe(() => {
        const currentRows = dataSource.getLogicalRowIdsSnapshot();
        const currentRowIds = new Set(currentRows);

        // Find deleted rows
        const deletedRowIds: string[] = [];
        for (const rowId of previousRowIds) {
          if (!currentRowIds.has(rowId)) {
            deletedRowIds.push(rowId);
          }
        }

        // Update cache for ALL existing rows (not just new ones)
        // This ensures cache stays in sync with cell value changes
        for (const rowId of currentRowIds) {
          this.updateRowCache(databaseId, dataSource, rowId);
        }

        // Queue cleanup for deleted rows
        if (deletedRowIds.length > 0) {
          console.log(
            `[RelationIntegrity] Detected ${deletedRowIds.length} deleted rows in database ${databaseId}`
          );
          this.cleanupQueue$.next({ docId, databaseId, deletedRowIds });
        }

        previousRowIds = currentRowIds;
      });

      // Store subscription for cleanup
      this.databaseSubscriptions.set(databaseId, subscription);
    } catch (e) {
      console.warn(
        `[RelationIntegrity] Failed to subscribe to database ${databaseId}:`,
        e
      );
    }
  }

  /**
   * Unsubscribe from a database.
   */
  private unsubscribeFromDatabase(databaseId: string): void {
    const unsubscribe = this.databaseSubscriptions.get(databaseId);
    if (unsubscribe) {
      unsubscribe(); // BlockSuite subscriptions return () => void
      this.databaseSubscriptions.delete(databaseId);
    }
    this.relationCache.delete(databaseId);
  }

  /**
   * Update the relation cache for all rows in a database.
   */
  private updateRelationCache(
    databaseId: string,
    dataSource: DatabaseBlockDataSource
  ): void {
    const rowIds = dataSource.getLogicalRowIdsSnapshot();
    for (const rowId of rowIds) {
      this.updateRowCache(databaseId, dataSource, rowId);
    }
  }

  /**
   * Update cache for a single row.
   */
  private updateRowCache(
    databaseId: string,
    dataSource: DatabaseBlockDataSource,
    rowId: string
  ): void {
    const dbCache = this.relationCache.get(databaseId);
    if (!dbCache) return;

    const rowCache: RowRelationCache = {
      rowId,
      relations: new Map(),
    };

    // Find all relation columns
    const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    for (const propId of propertyIds) {
      const propType = dataSource.propertyTypeGet(propId);
      if (propType !== 'relation') continue;

      const rawPropData = dataSource.propertyDataGet(propId);
      const propData = rawPropData as unknown as RelationPropertyData | null;
      if (!propData?.targetDatabaseId) continue;

      // Get the cell value
      const cellValue = dataSource.cellValueGet(rowId, propId);
      const values = this.normalizeToArray(cellValue);

      if (values.length > 0) {
        rowCache.relations.set(propId, {
          targetDocId: propData.targetDocId,
          targetDatabaseId: propData.targetDatabaseId,
          inverseFieldId: propData.inverseFieldId,
          inverseCardinality: propData.inverseCardinality ?? 'many',
          values,
        });
      }
    }

    // Only cache if there are relations
    if (rowCache.relations.size > 0) {
      dbCache.set(rowId, rowCache);
    } else {
      dbCache.delete(rowId);
    }
  }

  /**
   * Process deleted rows and cleanup inverse references.
   */
  private async processDeletedRows(
    sourceDocId: string,
    sourceDatabaseId: string,
    deletedRowIds: string[]
  ): Promise<void> {
    const dbCache = this.relationCache.get(sourceDatabaseId);
    if (!dbCache) return;

    for (const rowId of deletedRowIds) {
      const rowCache = dbCache.get(rowId);
      if (!rowCache) {
        console.log(
          `[RelationIntegrity] No cached relations for deleted row ${rowId}`
        );
        continue;
      }

      // Process each relation column
      for (const [, relationInfo] of rowCache.relations) {
        const { targetDocId, targetDatabaseId, inverseFieldId, values } =
          relationInfo;

        if (!inverseFieldId) {
          // No inverse relation configured, skip
          continue;
        }

        console.log(
          `[RelationIntegrity] Cleaning up ${values.length} inverse refs for deleted row ${rowId}`
        );

        // Remove the inverse reference for each target row
        for (const targetRowId of values) {
          try {
            await this.removeInverseReference(
              sourceDocId,
              rowId, // The deleted row ID - this is what we're removing from inverse
              targetDocId,
              targetDatabaseId,
              targetRowId,
              inverseFieldId
            );
          } catch (e) {
            console.warn(
              `[RelationIntegrity] Failed to remove inverse ref for row ${targetRowId}:`,
              e
            );
          }
        }
      }

      // Remove from cache
      dbCache.delete(rowId);
    }
  }

  /**
   * Remove an inverse reference.
   * This removes `sourceRowId` from the cell at (targetRowId, inverseFieldId).
   */
  private async removeInverseReference(
    sourceDocId: string,
    sourceRowId: string,
    targetDocId: string | undefined,
    targetDatabaseId: string,
    targetRowId: string,
    inverseFieldId: string
  ): Promise<void> {
    const isCrossDoc =
      targetDocId && targetDocId !== sourceDocId && targetDocId.length > 0;

    if (isCrossDoc) {
      // Cross-doc: Use CrossDocRelationService
      // Get current value
      const getResult = await this.crossDocRelation.getCrossDocCellValue(
        targetDocId,
        targetDatabaseId,
        targetRowId,
        inverseFieldId
      );

      if (!getResult.success) {
        console.warn(
          `[RelationIntegrity] Failed to get cross-doc cell value:`,
          getResult.error
        );
        return;
      }

      const currentValue = getResult.value ?? [];
      const newValue = currentValue.filter(id => id !== sourceRowId);

      // Only update if value changed
      if (newValue.length !== currentValue.length) {
        const updateResult =
          await this.crossDocRelation.updateCrossDocCellValue(
            targetDocId,
            targetDatabaseId,
            targetRowId,
            inverseFieldId,
            newValue
          );

        if (!updateResult.success) {
          console.warn(
            `[RelationIntegrity] Failed to update cross-doc cell:`,
            updateResult.error
          );
        } else {
          console.log(
            `[RelationIntegrity] Removed inverse ref in cross-doc: ${targetDocId}/${targetRowId}`
          );
        }
      }
    } else {
      // InDoc: Direct BlockSuite operation
      try {
        const { doc, release } = this.docsService.open(sourceDocId);
        try {
          const blockSuiteDoc = doc.blockSuiteDoc;
          if (!blockSuiteDoc) return;

          const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
          const targetDb = databases.find(
            (db: { id: string }) => db.id === targetDatabaseId
          );

          if (!targetDb) return;

          const model =
            (targetDb as unknown as { model?: DatabaseBlockModel }).model ??
            targetDb;
          const dataSource = createDatabaseBlockDataSource(
            model as DatabaseBlockModel
          );

          const currentValue = dataSource.cellValueGet(
            targetRowId,
            inverseFieldId
          );
          const currentArray = this.normalizeToArray(currentValue);
          const newValue = currentArray.filter(id => id !== sourceRowId);

          // Only update if value changed
          if (newValue.length !== currentArray.length) {
            dataSource.cellValueChange(targetRowId, inverseFieldId, newValue);
            console.log(
              `[RelationIntegrity] Removed inverse ref in local db: ${targetRowId}`
            );
          }
        } finally {
          release();
        }
      } catch (e) {
        console.warn(
          `[RelationIntegrity] Failed to remove local inverse ref:`,
          e
        );
      }
    }
  }

  /**
   * Normalize a cell value to string array.
   */
  private normalizeToArray(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [String(value)];
  }

  /**
   * Cleanup when service is disposed.
   */
  override dispose(): void {
    // Unsubscribe from database discovery
    if (this.discoverySubscription) {
      this.discoverySubscription.unsubscribe();
      this.discoverySubscription = null;
    }

    // Unsubscribe from cleanup queue
    if (this.cleanupSubscription) {
      this.cleanupSubscription.unsubscribe();
      this.cleanupSubscription = null;
    }

    // Unsubscribe from all databases
    for (const dbId of this.databaseSubscriptions.keys()) {
      this.unsubscribeFromDatabase(dbId);
    }

    // Release all docs
    for (const release of this.docReleases.values()) {
      release();
    }
    this.docReleases.clear();

    this.cleanupQueue$.complete();

    super.dispose();
  }

  /**
   * Force a cache update for a specific row.
   * Call this when a relation cell value changes.
   */
  updateRowRelationCache(
    docId: string,
    databaseId: string,
    rowId: string
  ): void {
    try {
      const { doc, release } = this.docsService.open(docId);
      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return;

        const databases = blockSuiteDoc.getBlocksByFlavour('affine:database');
        const dbBlock = databases.find(
          (db: { id: string }) => db.id === databaseId
        );

        if (!dbBlock) return;

        const model =
          (dbBlock as unknown as { model?: DatabaseBlockModel }).model ??
          dbBlock;
        const dataSource = createDatabaseBlockDataSource(
          model as DatabaseBlockModel
        );

        this.updateRowCache(databaseId, dataSource, rowId);
      } finally {
        release();
      }
    } catch (e) {
      console.warn(
        `[RelationIntegrity] Failed to update row cache for ${rowId}:`,
        e
      );
    }
  }
}
